import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  LinearProgress,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { myClassesServices } from "../../../services";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useViewport } from "../../../helpers";
import {
  DirectionsRun,
  ExpandMore,
  Favorite,
  Person,
  RocketLaunch,
  SendToMobile,
} from "@mui/icons-material";
import { format } from "date-fns";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ClassesManagementCharts from "../../classManagement/ClassManagementCharts";
import Paper from "@mui/material/Paper";
import School from "../../../assets/school-classes.json";
import Lottie from "lottie-react";

const ClassStats = (props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { width } = useViewport();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const observer = useRef(null);
  const [groups, setGroups] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  const loadMoreData = useCallback(() => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    myClassesServices
      .getTimeSpentForMyGroups(props.exparience._id, props.groupId)
      .then(({ groups, timeSpentByMembers }) => {
        setGroups(enrichGroups(groups, timeSpentByMembers));
        myClassesServices
          .getAllStatisticsForMyGroups(
            props.exparience._id,
            page,
            props.groupId
          )
          .then((data) => {
            setData((prev) => [...prev, ...enrichData(data)]);
            setHasMore(data.data.length > 0);
            setPage((prev) => prev + 1);
            setIsLoading(false);
          })
          .catch((err) => {
            enqueueSnackbar(t("Error fetching data"), { variant: "error" });
            setIsLoading(false);
          });
      });
  }, [page, t, enqueueSnackbar, isLoading, hasMore]);

  const enrichGroups = (groups, timeSpentByMembers) => {
    const userMap = Object.fromEntries(
      timeSpentByMembers.map((user) => [user.userId, user])
    );
    return groups.map((item) => ({
      ...item,
      members:
        item.members.map((member) => ({
          ...member,
          statistics: member.account ? userMap[member.account.account_id] : {},
        })) || [],
    }));
  };

  const enrichData = (response) => {
    const userMap = Object.fromEntries(
      response.users.map((user) => [user.account_id, user])
    );
    return response.data.map((item) => ({
      ...item,
      user: userMap[item.userId] || {},
      formattedDate: format(new Date(item.createdDate), "dd/MM/yyyy HH:mm"),
      type: formatType(item.type),
    }));
  };

  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreData();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, loadMoreData]
  );

  useEffect(() => {
    loadMoreData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const formatType = (type) => {
    switch (type) {
      case "launch":
        return <RocketLaunch />;
      case "paces":
        return <DirectionsRun />;
      case "share":
        return <SendToMobile />;
      case "like":
        return <Favorite />;
      default:
        return <Avatar />;
    }
  };

  const formatTimeSpent = (seconds) => {
    if (seconds < 60) {
      return `${seconds.toFixed(0)} s`; // Afficher uniquement les secondes
    }
    const minutes = Math.floor(seconds / 60); // Calcul des minutes
    const remainingSeconds = Math.floor(seconds % 60); // Calcul des secondes restantes
    return `${minutes} min ${remainingSeconds} s`; // Afficher minutes et secondes
  };

  if ((!data || data.length === 0) && isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
        <CircularProgress />
        <Typography ml={2}>{t("loaders.fetching")}</Typography>
      </Box>
    );
  }

  const MyAccordion = ({ groupId, group, width, props }) => {
    if (groupId !== null && groupId !== undefined) {
      return (
        <ClassesManagementCharts
          myClass={{
            ...group,
            duration: props.exparience.groups.find(
              (g) => g.groupId === group.id
            ).duration,
          }}
          standalone={true}
          key={group._id}
        />
      );
    }
    return (
      <Accordion sx={{ marginBottom: "1rem" }} elevation={8}>
        {/* Header of the Accordion */}
        <AccordionSummary
          expandIcon={
            groupId !== null && groupId !== undefined ? (
              <></>
            ) : (
              <ExpandMore sx={{ color: "white" }} />
            )
          }
          sx={{
            borderBottom: "1px solid rgba(255, 255, 255, 0.18)",
            padding: "0.5rem 0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: width < 768 ? "140px" : "300px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {group.imageUrl ? (
              <img
                src={group.imageUrl}
                alt={group.imageUrl}
                style={{
                  minWidth: "50px",
                  width: "50px",
                  height: "50px",
                  borderRadius: "10px",
                }}
              />
            ) : (
              <Lottie
                animationData={School}
                loop={false}
                style={{ width: "50px", height: "50px" }}
              />
            )}
            <Typography
              variant="h6"
              sx={{
                minWidth: "100px",
                marginLeft: "1rem",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {group.name}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body1" sx={{ marginLeft: "1rem" }}>
              {group.members.length}
            </Typography>
            <Person sx={{ marginLeft: "0.25rem" }} />
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: 2,
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: "1rem",
              fontWeight: "bold",
            }}
          >
            <Typography variant="body1" sx={{ marginLeft: "1rem" }}>
              {formatTimeSpent(
                group.members
                  .map((m) => m.statistics?.totalTimeSpent ?? 0)
                  .reduce((a, b) => a + b, 0)
              )}
            </Typography>
          </Box>
        </AccordionSummary>

        {/* Details that will be shown when expanded */}
        <AccordionDetails
          sx={{ padding: 0, borderTop: "1px solid rgba(255, 255, 255, 0.18)" }}
        >
          <ClassesManagementCharts
            myClass={{
              ...group,
              duration: props.exparience.groups.find(
                (g) => g.groupId === group.id
              ).duration,
            }}
            standalone={true}
            key={group._id}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box
      sx={{ maxWidth: "800px", margin: "0 auto", padding: { xs: 0, sm: 2 } }}
    >
      {groups.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "#2A2A2A",
            borderRadius: 1,
            margin: "0 auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Box sx={{ mr: 2 }}>
              {groups[0].imageUrl ? (
                <img
                  src={groups[0].imageUrl}
                  alt={groups[0].imageUrl}
                  style={{
                    minWidth: "50px",
                    width: "50px",
                    height: "50px",
                    borderRadius: "10px",
                  }}
                />
              ) : (
                <Lottie
                  animationData={School}
                  loop={false}
                  style={{ width: "50px", height: "50px" }}
                />
              )}
            </Box>
            <Typography variant="h6">{groups[0].name}</Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {width < 769
              ? t("classManagement.statistics.masterclassDuration")
              : t("classManagement.statistics.duration")}
            {props.exparience.groups[0].duration}{" "}
            {t("classManagement.statistics.minutes")}
          </Typography>

          {(() => {
            // Calcul de l'avancement global
            const totalDuration = props.exparience.groups[0].duration * 60; // Conversion en secondes
            const members = groups[0].members || [];

            if (members.length === 0) return null;

            // Calcul de la moyenne du temps passé
            const totalTimeSpent = members.reduce((sum, member) => {
              return (
                sum +
                (!member.statistics?.totalTimeSpent
                  ? 0
                  : member.statistics?.totalTimeSpent > totalDuration
                  ? totalDuration
                  : member.statistics?.totalTimeSpent)
              );
            }, 0);

            const averageTimeSpent = totalTimeSpent / members.length;
            const progressPercentage = Math.min(
              100,
              (averageTimeSpent / totalDuration) * 100
            );

            return (
              <Box sx={{ width: "100%", position: "relative" }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t("classManagement.statistics.globalProgress")}:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    position: "absolute",
                    top: "80%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2,
                    color: "#fff",
                    textShadow: "0px 0px 2px rgba(0,0,0,0.5)",
                  }}
                >
                  {Math.round(progressPercentage)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 18,
                    borderRadius: 5,
                    backgroundColor:
                      progressPercentage >= 100
                        ? "#4A90E240"
                        : progressPercentage >= 50
                        ? "#7ED32140"
                        : progressPercentage >= 21
                        ? "#F5A62340"
                        : "#D0021B40",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor:
                        progressPercentage >= 100
                          ? "#4A90E2"
                          : progressPercentage >= 50
                          ? "#7ED321"
                          : progressPercentage >= 21
                          ? "#F5A623"
                          : "#D0021B",
                    },
                  }}
                />
              </Box>
            );
          })()}
        </Box>
      )}

      <Tabs value={tabIndex} onChange={handleTabChange} centered>
        <Tab label={t("classManagement.statistics.general")} />
        <Tab label={t("classManagement.statistics.activity")} />
      </Tabs>
      {tabIndex === 0 && (
        <Card
          elevation={12}
          sx={{
            overflowY: "auto",
            margin: "0 auto",
            backgroundColor: "#333333",
            "&::-webkit-scrollbar": {
              width: "5px",
              height: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D3AF5F",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgb(181 154 79 / 58%)",
              borderRadius: "10px",
            },
          }}
        >
          {groups.map((group, index) => {
            return (
              <MyAccordion
                key={`${group.id}-${index + 1}`}
                groupId={props.groupId}
                group={group}
                width={width}
                props={props}
              />
            );
          })}
        </Card>
      )}
      {tabIndex === 1 && (
        <Card elevation={4}>
          {data.length === 0 && (
            <Typography
              variant="body1"
              sx={{ padding: "1rem", textAlign: "center" }}
            >
              {t("classManagement.noData")}
            </Typography>
          )}
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow
                    key={row._id}
                    ref={index === data.length - 1 ? lastElementRef : null}
                  >
                    <TableCell size={"small"}>{index + 1}</TableCell>
                    <TableCell>
                      <Avatar
                        src={row.user.avatarUrl || ""}
                        alt={row.user.details?.firstname || ""}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        display: { xs: "none", sm: "table-cell" }, // Caché sur mobile, visible à partir de "small" (sm) screen
                      }}
                    >
                      {row.user.details?.lastname || t("N/A")}
                    </TableCell>
                    <TableCell>
                      {row.user.details?.firstname || t("N/A")}
                    </TableCell>
                    <TableCell>{row.type || t("Unknown")}</TableCell>
                    <TableCell>{row.formattedDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
      {isLoading && (
        <Box mt={2} display="flex" justifyContent="center" alignItems="center">
          <CircularProgress size={30} />
        </Box>
      )}
    </Box>
  );
};

export default ClassStats;
